import { FssPreviewModals } from "@mirage/growth/components/modals";
import { TrialEndingModalWrapper } from "@mirage/mosaics/TrialEndingModal/TrialEndingModal";
import { useApiEnvironment } from "@mirage/service-auth/useApiEnvironment";
import useDropboxAccount from "@mirage/service-auth/useDropboxAccount";
import { useIsLoggedIn } from "@mirage/service-auth/useDropboxAuthentication";
import { useFeatureFlagValue } from "@mirage/service-experimentation/useFeatureFlagValue";
import { convertFeatureValueToBool } from "@mirage/service-experimentation/util";
import { SurveyProvider } from "@mirage/service-feedback/hooks/SurveyProvider";
import { useEsvNavigationInterceptor } from "@mirage/service-fss-native/hooks/useEsvNavigationInterceptor";
import { logPageLoadMilestoneOnce } from "@mirage/service-operational-metrics/page-load";
import { SearchContextProvider } from "@mirage/service-search/hooks/useSearchContext";
import { privacyCategoryStateAtom } from "@mirage/shared/privacy";
import { useIsHoverFriendly } from "@mirage/shared/responsive/hover";
import { activeStackAtom } from "@mirage/stacks/atoms";
import { getIsPublicStack } from "@mirage/stacks/utils";
import { StartPageFocusStateLoader } from "@mirage/webapp/analytics/sessionManagers/StartPageFocusStateLoader";
import { WebLayout } from "@mirage/webapp/components/WebLayout";
import { getEligibilityFromCache } from "@mirage/webapp/gating/Eligibility";
import { WebEligibilityCheck } from "@mirage/webapp/gating/WebEligibilityCheck";
import { useInitAuthedJobs } from "@mirage/webapp/hooks/useInitAuthedJobs";
import { isConsentAccepted } from "@mirage/webapp/hooks/usePrivacyModule";
import { PageType } from "@mirage/webapp/routeTypes";
import { useAtomValue } from "jotai";
import { memo } from "react";
import { useEsvNavigation } from "../fss-native/useEsvNavigation";
import { getProductLoggingSurface } from "../helpers/productLoggingConfig";
import { useRoute } from "../Routes";
import { sharedWorkerDatabase } from "../shared-worker/sharedWorkerStorage";
import { Config } from "../shared/config";

function getEligibilityFromStore() {
  return getEligibilityFromCache(sharedWorkerDatabase);
}

export const AuthedContainer: React.FC = memo(function AuthedContainer() {
  logPageLoadMilestoneOnce("AuthedContainer start");

  const route = useRoute();
  const account = useDropboxAccount();
  const privacyCategoryState = useAtomValue(privacyCategoryStateAtom);
  const isAnalyticsEnabled = isConsentAccepted(privacyCategoryState);

  const activeStack = useAtomValue(activeStackAtom);
  const isPublicStack = getIsPublicStack(activeStack);
  const isHoverFriendly = useIsHoverFriendly();
  const isStackRemoveEligibilityCheckEnabled = convertFeatureValueToBool(
    useFeatureFlagValue("dash_web_2025_02_07_stack_remove_eligibility_check"),
  );

  useInitAuthedJobs(route.pattern, Config.DEVBOX);
  useApiEnvironment();

  useEsvNavigationInterceptor();

  // Handle ESV navigation message processing for all authenticated pages
  const loggedIn = useIsLoggedIn();
  useEsvNavigation(loggedIn);

  if (route.pageType === PageType.LOGOUT) {
    return <route.Component />;
  }

  const shouldCheckWebEligibility = isStackRemoveEligibilityCheckEnabled
    ? route.pageType !== PageType.STACK
    : route.pageType !== PageType.STACK || isPublicStack === false;

  return (
    <>
      <SurveyProvider
        surface={getProductLoggingSurface()}
        environmentId={Config.SPRIG_ENVIRONMENT_ID}
        disabled={!isAnalyticsEnabled}
        publicAccountId={
          account?.public_account_id && atob(account?.public_account_id)
        }
      />
      <SearchContextProvider isLocalFilesEnabled={false}>
        {shouldCheckWebEligibility && (
          <WebEligibilityCheck
            getEligibilityFromStore={getEligibilityFromStore}
            isDevboxBackend={Config.ENV === "devbox"}
          />
        )}

        {route.pageType !== PageType.SETUP && isHoverFriendly && (
          // Don't show the welcome modal in touch-only screens or the onboarding page
          // to ensure that the first time users will see it will be when they're ready
          // to interact with the app features
          <FssPreviewModals />
        )}

        {/* TODO: verify that FssPreviewModals aren't shown (OTCGA-817) */}
        <TrialEndingModalWrapper />

        <StartPageFocusStateLoader />

        {route.wholePage ? (
          <route.Component />
        ) : (
          <WebLayout hideTopNav={route.hideTopNav}>
            <route.Component />
          </WebLayout>
        )}
      </SearchContextProvider>
    </>
  );
});
